import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  CampaignResponse,
  CampaignSummary,
  CreateCampaignRequest,
  CreateCampaignResponse,
  GetAllCampaignRequest,
  UpdateCampaignRequest,
} from 'redux/types/Campaign.types';
import { campaignEndpoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

import {
  createCampaign,
  createCampaignFailure,
  createCampaignSuccess,
  fetchAllCampaigns,
  fetchAllCampaignsFailure,
  fetchAllCampaignsSuccess,
  fetchCampaignSummary,
  fetchCampaignSummaryFailure,
  fetchCampaignSummarySuccess,
  pauseCampaigns,
  pauseCampaignsFailure,
  pauseCampaignsSuccess,
  startCampaigns,
  startCampaignsFailure,
  startCampaignsSuccess,
  updateCampaign,
  updateCampaignFailure,
  updateCampaignSuccess,
} from '../../slice/campaign/Campaign.slice';
import { handleError } from '../utils/errorUtils';

// Saga for creating a campaign
function* createCampaignSaga(action: PayloadAction<CreateCampaignRequest>) {
  try {
    const response: AxiosResponse<CreateCampaignResponse> =
      yield HTTPClient.PostRequest(
        campaignEndpoints.CREATE_CAMPAIGN(),
        action.payload
      );
    yield put(createCampaignSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, createCampaignFailure, '');
  }
}

// Saga for updating a campaign
function* updateCampaignSaga(action: PayloadAction<UpdateCampaignRequest>) {
  try {
    const response: AxiosResponse<CreateCampaignResponse> =
      yield HTTPClient.PutRequest(
        campaignEndpoints.UPDATE_CAMPAIGN(action.payload.id),
        action.payload.payload
      );
    yield put(updateCampaignSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, updateCampaignFailure, '');
  }
}

// Saga for fetching campaign details
function* fetchAllCampaignsSaga(action: PayloadAction<GetAllCampaignRequest>) {
  try {
    const { limit, offset, searchValue } = action.payload;
    const response: AxiosResponse<CampaignResponse> =
      yield HTTPClient.GetRequest(
        campaignEndpoints.GET_ALL_CAMPAIGN(limit, offset, searchValue)
      );
    yield put(fetchAllCampaignsSuccess(response.data));
  } catch (error: any) {
    yield put(fetchAllCampaignsFailure(error?.message));
    message.error('Failed to fetch campaign details');
  }
}

function* fetchAgentSummarySaga() {
  try {
    const response: AxiosResponse<CampaignSummary> = yield call(
      HTTPClient.GetRequest,
      campaignEndpoints.GET_CAMPAIGN_SUMMARY
    );
    yield put(fetchCampaignSummarySuccess(response.data));
    message.success('Agent summary fetched successfully');
  } catch (error: any) {
    yield put(
      fetchCampaignSummaryFailure(
        error?.message || 'Failed to fetch agent summary'
      )
    );
    message.error('Failed to fetch agent summary');
  }
}

function* startCampaignSaga(action: PayloadAction<string>) {
  try {
    const campaignId = action.payload;
    const response: AxiosResponse<any> = yield call(
      HTTPClient.PostRequest,
      campaignEndpoints.START_CAMPAIGN(campaignId)
    );
    yield put(startCampaignsSuccess());
    message.success('Camoaign stared successfully');
  } catch (error: any) {
    yield put(startCampaignsFailure());
    message.error('Error while starting the campaign!');
  }
}

function* pauseCampaignSaga(action: PayloadAction<string>) {
  try {
    const campaignId = action.payload;
    const response: AxiosResponse<any> = yield call(
      HTTPClient.PostRequest,
      campaignEndpoints.PAUSE_CAMPAIGN(campaignId)
    );
    yield put(pauseCampaignsSuccess());
    message.success('Camoaign paused successfully');
  } catch (error: any) {
    yield put(pauseCampaignsFailure());
    message.error('Error while pausing the campaign!');
  }
}

export function* watchCampaignSagas() {
  yield takeLatest(createCampaign.type, createCampaignSaga);
  yield takeLatest(updateCampaign.type, updateCampaignSaga);
  yield takeLatest(fetchCampaignSummary.type, fetchAgentSummarySaga);
  yield takeLatest(fetchAllCampaigns.type, fetchAllCampaignsSaga);
  yield takeLatest(startCampaigns.type, startCampaignSaga);
  yield takeLatest(pauseCampaigns.type, pauseCampaignSaga);
}
